import { forwardRef, useImperativeHandle } from 'react';
import { TranslationService } from '../../../services/translation.service';
import ValidationInfo from '../../../validation/validation-info';
import type IValidatable from '../validatable.interface';
import type { IServerValidationError } from '../validatable.interface';
import ValidationEngine from '../../../validation/validation-engine';
import { PaymentMethod } from '../../../models/payment-method.enum';
import TextFieldFormGroup from '../TextFieldFormGroup';
import { PaymentMethodSelectorField } from './payment-method-selector-field.enum';
import LogService from '../../../services/log-service/log.service';

export interface IPaymentMethodSelectorState {
  value: {
    paymentMethod: PaymentMethod;
    travelCardNumber: string;
    toGoTicketNumber: string;
    otherTicketType: string;
  };
  validationInfo: ValidationInfo;
}

interface IPaymentMethodSelectorProps {
  state: IPaymentMethodSelectorState;
  heading?: string;
  availablePaymentMethods?: PaymentMethod[];
  radioLabelTranslation?: (paymentMethod: PaymentMethod) => string;
  onChange?: (value: { paymentMethod: PaymentMethod; travelCardNumber: string; toGoTicketNumber: string; otherTicketType: string }) => void;
  onKeyUp?: ((field: PaymentMethodSelectorField) => void) | undefined;
}

function getDefaultRadioLabelTranslation(paymentMethod: PaymentMethod): string {
  switch (paymentMethod) {
    case PaymentMethod.ToGo:
      return TranslationService.get('customerserviceforms.paymentmethod.togopaymentmethod');
    case PaymentMethod.TravelCard:
      return TranslationService.get('customerserviceforms.paymentmethod.travelcardpaymentmethod');
    case PaymentMethod.OtherTicket:
      return TranslationService.get('customerserviceforms.paymentmethod.otherticketpaymentmethod');
    default:
      throw new Error(`Unexpected payment method: ${paymentMethod}`);
  }
}

function getTextInputLabelTranslationKey(paymentMethod: PaymentMethod): string {
  switch (paymentMethod) {
    case PaymentMethod.ToGo:
      return 'customerserviceforms.paymentmethod.togoticketnumber';
    case PaymentMethod.TravelCard:
      return 'customerserviceforms.paymentmethod.travelcardnumber';
    case PaymentMethod.OtherTicket:
      return 'customerserviceforms.paymentmethod.otherticketype';
    default:
      throw new Error(`Unexpected payment method: ${paymentMethod}`);
  }
}

function getValue(state: IPaymentMethodSelectorState, paymentMethod: PaymentMethod): string {
  switch (paymentMethod) {
    case PaymentMethod.ToGo:
      return state.value.toGoTicketNumber;
    case PaymentMethod.TravelCard:
      return state.value.travelCardNumber;
    case PaymentMethod.OtherTicket:
      return state.value.otherTicketType;
    default:
      throw new Error(`Unexpected payment method: ${paymentMethod}`);
  }
}

function getValidationInfo(state: IPaymentMethodSelectorState, paymentMethod: PaymentMethod): ValidationInfo {
  switch (paymentMethod) {
    case PaymentMethod.ToGo:
      return state.validationInfo.children.toGoTicketNumber ?? new ValidationInfo();
    case PaymentMethod.TravelCard:
      return state.validationInfo.children.travelCardNumber ?? new ValidationInfo();
    case PaymentMethod.OtherTicket:
      return state.validationInfo.children.otherTicketType ?? new ValidationInfo();
    default:
      throw new Error(`Unexpected payment method: ${paymentMethod}`);
  }
}

const defaultAvailablePaymentMethods = [PaymentMethod.ToGo, PaymentMethod.TravelCard, PaymentMethod.OtherTicket];

export default forwardRef<IValidatable, IPaymentMethodSelectorProps>(function PaymentMethodSelector(
  { state, heading, availablePaymentMethods = defaultAvailablePaymentMethods, radioLabelTranslation = getDefaultRadioLabelTranslation, onChange, onKeyUp },
  ref,
) {
  // === INTERFACE IMPLEMENTATION ===
  useImperativeHandle(ref, () => ({
    checkValidity(): ValidationInfo {
      const children: { [key: string]: ValidationInfo } = {};
      if (availablePaymentMethods.includes(PaymentMethod.ToGo)) {
        const toGoTicketNumberValidationInfo = new ValidationInfo(`${radioLabelTranslation(PaymentMethod.ToGo)} - `);
        ValidationEngine.for(toGoTicketNumberValidationInfo)
          .requiredIf(state.value.paymentMethod === PaymentMethod.ToGo, state.value.toGoTicketNumber)
          .validate();
        children.toGoTicketNumber = toGoTicketNumberValidationInfo;
      }
      if (availablePaymentMethods.includes(PaymentMethod.TravelCard)) {
        const travelCardNumberValidationInfo = new ValidationInfo(`${radioLabelTranslation(PaymentMethod.TravelCard)} - `);
        ValidationEngine.for(travelCardNumberValidationInfo)
          .requiredIf(state.value.paymentMethod === PaymentMethod.TravelCard, state.value.travelCardNumber)
          .validate();
        children.travelCardNumber = travelCardNumberValidationInfo;
      }
      if (availablePaymentMethods.includes(PaymentMethod.OtherTicket)) {
        const otherTicketTypeValidationInfo = new ValidationInfo(`${radioLabelTranslation(PaymentMethod.OtherTicket)} - `);
        ValidationEngine.for(otherTicketTypeValidationInfo)
          .requiredIf(state.value.paymentMethod === PaymentMethod.OtherTicket, state.value.otherTicketType)
          .validate();
        children.otherTicketType = otherTicketTypeValidationInfo;
      }
      return new ValidationInfo('', [], children);
    },
    handleServerValidationErrors(errors: IServerValidationError[]): ValidationInfo {
      return new ValidationInfo('', [], {
        toGoTicketNumber: new ValidationInfo(
          `${radioLabelTranslation(PaymentMethod.ToGo)} - `,
          errors
            .filter((e) => e.name === 'paymentMethodToGoTicketNumber')
            .map((e) => e.errorMessages)
            .reduce((a, b) => a.concat(b), []),
        ),
        travelCardNumber: new ValidationInfo(
          `${radioLabelTranslation(PaymentMethod.TravelCard)} - `,
          errors
            .filter((e) => e.name === 'paymentMethodTravelCardNumber')
            .map((e) => e.errorMessages)
            .reduce((a, b) => a.concat(b), []),
        ),
        otherTicketType: new ValidationInfo(
          `${radioLabelTranslation(PaymentMethod.OtherTicket)} - `,
          errors
            .filter((e) => e.name === 'paymentMethodOtherTicketType')
            .map((e) => e.errorMessages)
            .reduce((a, b) => a.concat(b), []),
        ),
      });
    },
  }));

  // === EVENT HANDLERS ===
  function handleTextFieldOnChange(paymentMethod: PaymentMethod, value: string): void {
    switch (paymentMethod) {
      case PaymentMethod.ToGo:
        onChange?.({ ...state.value, toGoTicketNumber: value });
        break;
      case PaymentMethod.TravelCard:
        onChange?.({ ...state.value, travelCardNumber: value });
        break;
      case PaymentMethod.OtherTicket:
        onChange?.({ ...state.value, otherTicketType: value });
        break;
      default:
        throw new Error(`Unexpected payment method: ${paymentMethod}`);
    }
  }

  function handleTextFieldKeyUp(paymentMethod: PaymentMethod): void {
    switch (paymentMethod) {
      case PaymentMethod.ToGo:
        onKeyUp?.(PaymentMethodSelectorField.toGoTicketNumber);
        break;
      case PaymentMethod.TravelCard:
        onKeyUp?.(PaymentMethodSelectorField.travelCardNumber);
        break;
      case PaymentMethod.OtherTicket:
        onKeyUp?.(PaymentMethodSelectorField.otherTicketType);
        break;
      default:
        throw new Error(`Unexpected payment method: ${paymentMethod}`);
    }
  }

  return (
    <div className="payment-method-selector">
      <fieldset>
        <legend className="h4 mb-3">{heading || TranslationService.get('customerserviceforms.paymentmethod.heading')}</legend>
        <div className="d-flex flex-column gap-3">
          {availablePaymentMethods.map((paymentMethod) => (
            <div key={paymentMethod} className={`payment-method-option ${getValidationInfo(state, paymentMethod).isInvalid ? 'is-invalid' : ''}`}>
              <div className="form-check">
                {/* eslint-disable-next-line jsx-a11y/role-supports-aria-props */}
                <input
                  type="radio"
                  className={`form-check-input ${state.validationInfo.isInvalid ? 'is-invalid' : ''}`}
                  name="payment-method"
                  id={`payment-method-${paymentMethod.toLocaleLowerCase()}`}
                  value={paymentMethod}
                  checked={state.value.paymentMethod === paymentMethod}
                  onChange={() => {
                    onKeyUp?.(PaymentMethodSelectorField.paymentMethod);
                    onChange?.({ ...state.value, paymentMethod });
                  }}
                  aria-invalid={state.validationInfo.isInvalid}
                  aria-describedby={state.validationInfo.isInvalid ? 'payment-method-validation-error' : undefined}
                />
                <label className="form-check-label" htmlFor={`payment-method-${paymentMethod.toLocaleLowerCase()}`}>
                  {radioLabelTranslation(paymentMethod)}
                </label>
              </div>
              {state.value.paymentMethod === paymentMethod && (
                <TextFieldFormGroup
                  inputId={`payment-method-${paymentMethod.toLocaleLowerCase()}-text-input`}
                  labelText={TranslationService.get(getTextInputLabelTranslationKey(paymentMethod))}
                  className="ps-4 mb-0 mt-2"
                  required={true}
                  state={{
                    value: getValue(state, paymentMethod),
                    validationInfo: getValidationInfo(state, paymentMethod),
                  }}
                  onChange={(value) => {
                    try {
                      handleTextFieldOnChange(paymentMethod, value);
                    } catch (error) {
                      LogService.logError(error);
                    }
                  }}
                  onKeyUp={() => {
                    try {
                      handleTextFieldKeyUp(paymentMethod);
                    } catch (error) {
                      LogService.logError(error);
                    }
                  }}
                />
              )}
            </div>
          ))}
        </div>
        {state.validationInfo.isInvalid && (
          <div id="payment-method-validation-error" className="invalid-feedback">
            {state.validationInfo.errors.map((error) => (
              <div key={error}>{error}</div>
            ))}
          </div>
        )}
      </fieldset>
    </div>
  );
});
